<template>
  <div class="admin-monthly-summary">
    <NavBar />
    <div class="container-fluid py-4">
      <div class="row">
        <div class="col-12">
          <div class="card shadow">
            <div class="card-header bg-white border-0">
              <div class="row align-items-center">
                <div class="col-12 col-md">
                  <h3 class="mb-0">Monthly Billing Summary</h3>
                </div>
                <div class="col-12 col-md-auto d-flex align-items-center mt-2 mt-md-0">
                  <b-form-select
                    v-model="selectedMonth"
                    :options="monthOptions"
                    class="mr-2"
                    style="width: 80px;"
                  />
                  <b-form-select
                    v-model="selectedYear"
                    :options="yearOptions"
                    style="width: 100px;"
                  />
                </div>
              </div>
            </div>
            <div class="card-body">
              <DailyRevenueChart
                v-if="!loading && !error"
                :rows="filteredRows"
                :year="selectedYear"
                :month="selectedMonth"
                class="mb-4 pb-3 border-bottom"
              />
              <div v-if="loading" class="text-center py-5">
                <b-spinner variant="primary" />
                <p class="mt-2">Loading...</p>
              </div>
              <div v-else-if="error" class="text-center py-5">
                <p class="text-danger">{{ error }}</p>
                <b-button variant="primary" @click="fetchData">Retry</b-button>
              </div>
              <div v-else-if="managerSummaries.length === 0" class="text-center py-5 text-muted">
                No billing data for {{ selectedMonth }}/{{ selectedYear }}.
              </div>
              <div v-else>
                <p class="text-muted mb-3">
                  Grand total: <strong>{{ grandTotal }} ILS</strong> across {{ managerSummaries.length }} manager(s)
                </p>

                <!-- Mobile cards (xs/sm only) -->
                <div class="d-md-none">
                  <div
                    v-for="summary in managerSummaries"
                    :key="summary.manager_id"
                    class="card mb-2"
                  >
                    <div class="card-body p-3">
                      <div class="d-flex justify-content-between align-items-start mb-2">
                        <div>
                          <strong>{{ summary.manager_name || 'Unknown' }}</strong>
                          <div class="text-muted small">{{ summary.manager_email || 'N/A' }}</div>
                        </div>
                        <span v-if="!summary.has_saved_card" class="badge badge-secondary">No card</span>
                        <span v-else-if="summary.charge_status === 'success'" class="badge badge-success">
                          <i class="fas fa-check mr-1"></i>Charged
                          <a v-if="summary.charge_document_url" :href="summary.charge_document_url" target="_blank" class="text-white ml-1" title="Receipt">
                            <i class="fas fa-file-invoice"></i>
                          </a>
                        </span>
                        <span v-else-if="summary.charge_status === 'failed'" class="badge badge-danger">Failed</span>
                        <span v-else class="badge badge-warning">Pending</span>
                      </div>
                      <div class="mb-3">
                        <strong class="text-primary">{{ summary.total.toFixed(2) }} {{ summary.currency }}</strong>
                        <span class="text-muted ml-2">· {{ summary.rides.length }} ride(s)</span>
                      </div>
                      <div class="d-flex">
                        <b-button
                          variant="outline-primary"
                          size="sm"
                          class="flex-grow-1 mr-2"
                          @click="openDetails(summary)"
                        >
                          <i class="fas fa-eye"></i> View
                        </b-button>
                        <b-button
                          v-if="summary.has_saved_card && summary.charge_status !== 'success'"
                          variant="success"
                          size="sm"
                          class="flex-grow-1"
                          :disabled="chargingManagerId === summary.manager_id"
                          @click="openChargeConfirm(summary)"
                        >
                          <i class="fas fa-credit-card mr-1"></i>
                          {{ chargingManagerId === summary.manager_id ? 'Charging…' : 'Charge' }}
                        </b-button>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Desktop table (md+) -->
                <div class="table-responsive d-none d-md-block">
                  <table class="table table-hover">
                    <thead class="thead-light">
                      <tr>
                        <th>Manager</th>
                        <th>Email</th>
                        <th>Month</th>
                        <th>Total Charges</th>
                        <th># Rides</th>
                        <th>Payment Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="summary in managerSummaries" :key="summary.manager_id">
                        <td><strong>{{ summary.manager_name || 'Unknown' }}</strong></td>
                        <td>{{ summary.manager_email || 'N/A' }}</td>
                        <td>{{ selectedMonth }}/{{ selectedYear }}</td>
                        <td><strong class="text-primary">{{ summary.total.toFixed(2) }} {{ summary.currency }}</strong></td>
                        <td>{{ summary.rides.length }}</td>
                        <td>
                          <span v-if="!summary.has_saved_card" class="badge badge-secondary">No card</span>
                          <span v-else-if="summary.charge_status === 'success'" class="badge badge-success">
                            <i class="fas fa-check mr-1"></i>Charged
                            <a v-if="summary.charge_document_url" :href="summary.charge_document_url" target="_blank" class="text-white ml-1" title="Receipt">
                              <i class="fas fa-file-invoice"></i>
                            </a>
                          </span>
                          <span v-else-if="summary.charge_status === 'failed'" class="badge badge-danger">Failed</span>
                          <span v-else class="badge badge-warning">Pending</span>
                        </td>
                        <td>
                          <b-button
                            variant="outline-primary"
                            size="sm"
                            class="mr-1"
                            @click="openDetails(summary)"
                          >
                            <i class="fas fa-eye"></i> View
                          </b-button>
                          <b-button
                            v-if="summary.has_saved_card && summary.charge_status !== 'success'"
                            variant="success"
                            size="sm"
                            :disabled="chargingManagerId === summary.manager_id"
                            @click="openChargeConfirm(summary)"
                          >
                            <i class="fas fa-credit-card mr-1"></i>
                            {{ chargingManagerId === summary.manager_id ? 'Charging…' : 'Approve & Charge' }}
                          </b-button>
                        </td>
                      </tr>
                      <tr class="table-secondary font-weight-bold">
                        <td colspan="3">Grand Total</td>
                        <td colspan="4">{{ grandTotal }} ILS</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Charge Confirmation Modal -->
    <b-modal
      v-if="chargeTarget"
      v-model="showChargeModal"
      title="Approve Monthly Charge"
      ok-title="Charge Now"
      ok-variant="success"
      cancel-title="Cancel"
      modal-class="monthly-summary-modal"
      @ok="executeCharge"
    >
      <p>You are about to charge <strong>{{ chargeTarget.manager_name }}</strong>:</p>
      <div class="alert alert-warning">
        <strong>{{ chargeTarget.total.toFixed(2) }} {{ chargeTarget.currency }}</strong>
        &nbsp;for {{ selectedMonth }}/{{ selectedYear }}
      </div>
      <p class="text-muted small">This will charge the saved card on SUMIT and send the manager a receipt by email.</p>
      <b-alert v-if="chargeError" variant="danger" show>{{ chargeError }}</b-alert>
    </b-modal>

    <!-- Ride Details Modal -->
    <b-modal
      v-if="selectedManager"
      v-model="showModal"
      :title="`Cost Breakdown — ${selectedManager.manager_name} (${selectedMonth}/${selectedYear})`"
      size="lg"
      ok-only
      ok-title="Close"
      modal-class="monthly-summary-modal"
    >
      <p class="mb-3">
        <strong>{{ selectedManager.manager_name }}</strong> &nbsp;|&nbsp; {{ selectedManager.manager_email }}
      </p>

      <!-- Charge Information -->
      <div v-if="selectedManager.charge_status" class="mb-4">
        <h6 class="mb-2">Payment Information</h6>
        <div :class="['alert', 'py-2', selectedManager.charge_status === 'success' ? 'alert-success' : 'alert-danger']">
          <div class="row">
            <div class="col-md-3">
              <strong>Status:</strong>
              <span :class="selectedManager.charge_status === 'success' ? 'text-success' : 'text-danger'">
                {{ selectedManager.charge_status === 'success' ? 'Charged' : 'Failed' }}
              </span>
            </div>
            <div v-if="selectedManager.charge_amount" class="col-md-3">
              <strong>Amount:</strong>
              {{ selectedManager.charge_amount.toFixed(2) }} {{ selectedManager.charge_currency }}
            </div>
            <div v-if="selectedManager.charge_at" class="col-md-3">
              <strong>Date:</strong>
              {{ new Date(selectedManager.charge_at).toLocaleString('en-GB') }}
            </div>
            <div v-if="selectedManager.charge_document_number" class="col-md-3">
              <strong>Document #:</strong> {{ selectedManager.charge_document_number }}
            </div>
            <div v-if="selectedManager.card_last4" class="col-md-3">
              <strong>Card:</strong> ****{{ selectedManager.card_last4 }}
            </div>
          </div>
          <div v-if="selectedManager.charge_document_url" class="mt-1">
            <a :href="selectedManager.charge_document_url" target="_blank">
              <i class="fas fa-file-invoice mr-1"></i>View Receipt
            </a>
          </div>
          <div v-if="selectedManager.charge_error" class="mt-1 text-danger small">
            Error: {{ selectedManager.charge_error }}
          </div>
        </div>
      </div>

      <b-alert v-if="selectedManager.charge_status === 'success'" variant="warning" show class="mb-3">
        This month has already been charged. Price edits are for record purposes only — the SUMIT receipt will not change.
      </b-alert>

      <div class="table-responsive">
        <table class="table table-sm table-bordered">
          <thead class="thead-light">
            <tr>
              <th class="d-none d-md-table-cell">Ride ID</th>
              <th>Ride</th>
              <th class="d-none d-md-table-cell">First Registration</th>
              <th class="d-none d-sm-table-cell">Days</th>
              <th>Participants</th>
              <th>Price</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="ride in selectedManager.rides" :key="ride.tx_id ? `manual_${ride.tx_id}` : (ride.ride_id != null ? ride.ride_id : 'white_label')">
              <td class="d-none d-md-table-cell">{{ ride.ride_id || '—' }}</td>
              <td>
                {{ ride.ride_title || '—' }}
                <span v-if="ride.description === 'manual_charge'" class="badge badge-info ml-1">Manual</span>
                <span v-else-if="ride.is_manually_adjusted" class="badge badge-warning ml-1">Admin adjusted</span>
              </td>
              <td class="d-none d-md-table-cell">{{ ride.first_registration ? ride.first_registration.slice(0, 10) : '—' }}</td>
              <td class="d-none d-sm-table-cell">{{ ride.charged_days || '—' }}</td>
              <td>
                {{ ride.participant_count }}
                <span v-if="ride.extra_user_count > 0" class="text-warning ml-1">
                  (+{{ ride.extra_user_count }} above 4)
                </span>
              </td>
              <td>
                <b-form-input
                  v-model.number="ride.ride_amount"
                  type="number"
                  size="sm"
                  min="0"
                  step="0.01"
                  style="width: 90px; display: inline-block;"
                />
                {{ ride.currency }}
              </td>
              <td>
                <b-button
                  size="sm"
                  variant="outline-primary"
                  :disabled="adjustingRideId === (ride.tx_id ? `manual_${ride.tx_id}` : (ride.ride_id != null ? ride.ride_id : 'white_label'))"
                  @click="saveRideAmount(ride)"
                >
                  <b-spinner v-if="adjustingRideId === (ride.tx_id ? `manual_${ride.tx_id}` : (ride.ride_id != null ? ride.ride_id : 'white_label'))" small />
                  <span v-else>Save</span>
                </b-button>
                <div v-if="adjustError[ride.tx_id ? `manual_${ride.tx_id}` : (ride.ride_id != null ? ride.ride_id : 'white_label')]" class="text-danger mt-1" style="font-size: 11px;">{{ adjustError[ride.tx_id ? `manual_${ride.tx_id}` : (ride.ride_id != null ? ride.ride_id : 'white_label')] }}</div>
                <div v-if="adjustSuccess[ride.tx_id ? `manual_${ride.tx_id}` : (ride.ride_id != null ? ride.ride_id : 'white_label')]" class="text-success mt-1" style="font-size: 11px;">Saved</div>
              </td>
            </tr>
            <tr class="table-secondary font-weight-bold">
              <td colspan="5">Total</td>
              <td colspan="2">{{ selectedManager.total.toFixed(2) }} {{ selectedManager.currency }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Users this manager brought to the site, with their usage this month -->
      <div v-if="selectedReferralGroup" class="mt-4">
        <h6 class="mb-2">
          Users referred by {{ selectedManager.manager_name || 'this manager' }}
          ({{ selectedReferralGroup.users.length }}) —
          <span class="text-primary">{{ selectedReferralGroup.total.toFixed(2) }} {{ selectedReferralGroup.currency || selectedManager.currency }}</span>
        </h6>
        <div class="table-responsive">
          <table class="table table-sm table-bordered">
            <thead class="thead-light">
              <tr>
                <th class="d-none d-md-table-cell">User ID</th>
                <th>User</th>
                <th class="d-none d-sm-table-cell">Email</th>
                <th>Referred on</th>
                <th class="d-none d-sm-table-cell">Lines</th>
                <th>Usage</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="u in selectedReferralGroup.users" :key="u.user_id">
                <td class="d-none d-md-table-cell">{{ u.user_id }}</td>
                <td>{{ u.name || 'Unknown' }}</td>
                <td class="d-none d-sm-table-cell">{{ u.email || 'N/A' }}</td>
                <td>{{ u.referred_at ? u.referred_at.slice(0, 10) : '—' }}</td>
                <td class="d-none d-sm-table-cell">{{ u.line_count }}</td>
                <td :class="u.total ? 'font-weight-bold' : 'text-muted'">
                  {{ u.total.toFixed(2) }} {{ u.currency || selectedReferralGroup.currency || selectedManager.currency }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div class="mt-3 p-3 border rounded bg-light">
        <strong class="d-block mb-2">Add custom line</strong>
        <div class="d-flex flex-column flex-sm-row align-items-stretch align-items-sm-center">
          <b-form-input
            v-model="newLine.label"
            placeholder="Label"
            size="sm"
            class="mb-2 mb-sm-0 mr-sm-2"
          />
          <b-form-input
            v-model.number="newLine.amount"
            type="number"
            placeholder="Amount"
            size="sm"
            min="0"
            step="0.01"
            class="mb-2 mb-sm-0 mr-sm-2"
            style="max-width: 120px;"
          />
          <b-button
            variant="primary"
            size="sm"
            :disabled="addingLine"
            @click="addBillingLine"
          >
            <b-spinner v-if="addingLine" small />
            <span v-else>Add</span>
          </b-button>
        </div>
        <div v-if="addLineError" class="text-danger mt-1 small">{{ addLineError }}</div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import NavBar from '@/components/NavBar.vue';
import DailyRevenueChart from '@/components/DailyRevenueChart.vue';
import billingApi from '@/api/billing';
import { INTERNAL_HOME_PATH } from '@/common/appHome';
import { buildReferralGroups } from '@/utils/referralGroups';


export default {
  name: 'AdminMonthlySummary',
  components: { NavBar, DailyRevenueChart },
  data() {
    const now = new Date();
    return {
      loading: false,
      error: null,
      rows: [],
      referrals: [],
      selectedYear: now.getFullYear(),
      selectedMonth: now.getMonth() + 1,
      showModal: false,
      selectedManager: null,
      showChargeModal: false,
      chargeTarget: null,
      chargeError: null,
      chargingManagerId: null,
      adjustingRideId: null,
      adjustError: {},
      adjustSuccess: {},
      newLine: { label: '', amount: null },
      addingLine: false,
      addLineError: null,
    };
  },
  computed: {
    ...mapGetters(['isUserManager', 'apptoken']),
    filteredRows() {
      return this.rows.filter(
        r => r.year === this.selectedYear && r.month === this.selectedMonth
      );
    },
    managerSummaries() {
      const map = {};
      for (const row of this.filteredRows) {
        const id = row.manager_id;
        if (!map[id]) {
          map[id] = {
            manager_id: id,
            manager_name: row.manager_name,
            manager_email: row.manager_email,
            currency: row.currency,
            total: 0,
            rides: [],
            has_saved_card: row.has_saved_card || false,
            card_last4: row.card_last4 || null,
            charge_status: row.charge_status || null,
            charge_amount: row.charge_amount || null,
            charge_currency: row.charge_currency || null,
            charge_document_url: row.charge_document_url || null,
            charge_document_number: row.charge_document_number || null,
            charge_at: row.charge_at || null,
            charge_error: row.charge_error || null,
          };
        }
        map[id].total += row.ride_amount;
        map[id].rides.push(row);
        if (row.has_saved_card) map[id].has_saved_card = true;
        if (row.card_last4) map[id].card_last4 = row.card_last4;
        if (row.charge_status) {
          map[id].charge_status = row.charge_status;
          map[id].charge_amount = row.charge_amount;
          map[id].charge_currency = row.charge_currency;
          map[id].charge_document_url = row.charge_document_url;
          map[id].charge_document_number = row.charge_document_number;
          map[id].charge_at = row.charge_at;
          map[id].charge_error = row.charge_error;
        }
      }
      return Object.values(map);
    },
    referralGroups() {
      return buildReferralGroups(this.referrals, this.filteredRows);
    },
    selectedReferralGroup() {
      if (!this.selectedManager) return null;
      return this.referralGroups.find(g => g.referrer_id === this.selectedManager.manager_id) || null;
    },
    grandTotal() {
      return this.filteredRows.reduce((sum, r) => sum + r.ride_amount, 0).toFixed(2);
    },
    yearOptions() {
      const years = [...new Set(this.rows.map(r => r.year))];
      if (!years.includes(this.selectedYear)) years.push(this.selectedYear);
      return years.sort((a, b) => b - a).map(y => ({ value: y, text: String(y) }));
    },
    monthOptions() {
      return Array.from({ length: 12 }, (_, i) => ({
        value: i + 1,
        text: String(i + 1).padStart(2, '0'),
      }));
    },
  },
  created() {
    if (!this.isUserManager) {
      this.$router.push(INTERNAL_HOME_PATH);
      return;
    }
    this.fetchData();
  },
  methods: {
    openDetails(summary) {
      this.selectedManager = summary;
      this.showModal = true;
    },
    openChargeConfirm(summary) {
      this.chargeTarget = summary;
      this.chargeError = null;
      this.showChargeModal = true;
    },
    async executeCharge(bvModalEvt) {
      bvModalEvt.preventDefault();
      const summary = this.chargeTarget;
      this.chargeError = null;
      this.chargingManagerId = summary.manager_id;
      try {
        const res = await billingApi.chargeMonthly(
          this.apptoken,
          summary.manager_id,
          this.selectedYear,
          this.selectedMonth,
          summary.total,
        );
        this.showChargeModal = false;
        const chargedAt = new Date().toISOString();
        const docUrl = res.data?.document_url || null;
        const docNumber = res.data?.document_number || null;
        this.rows = this.rows.map(row => {
          if (row.manager_id === summary.manager_id && row.year === this.selectedYear && row.month === this.selectedMonth) {
            return {
              ...row,
              charge_status: 'success',
              charge_amount: summary.total,
              charge_currency: summary.currency,
              charge_at: chargedAt,
              charge_document_url: docUrl,
              charge_document_number: docNumber,
            };
          }
          return row;
        });
      } catch (err) {
        const msg = err.response?.data?.message || 'Charge failed. Please try again.';
        this.chargeError = msg;
      } finally {
        this.chargingManagerId = null;
      }
    },
    async saveRideAmount(ride) {
      const key = ride.ride_id != null ? ride.ride_id : (ride.tx_id ? `manual_${ride.tx_id}` : 'white_label');
      this.adjustingRideId = key;
      this.$set(this.adjustError, key, null);
      this.$set(this.adjustSuccess, key, false);
      try {
        if (ride.description === 'manual_charge' && ride.tx_id) {
          await billingApi.updateBillingLine(this.apptoken, ride.tx_id, { amount: ride.ride_amount, label: ride.ride_title });
        } else {
          await billingApi.adjustRideAmount(this.apptoken, {
            userId: this.selectedManager.manager_id,
            rideId: ride.ride_id,
            year: this.selectedYear,
            month: this.selectedMonth,
            newAmount: ride.ride_amount,
          });
        }
        this.$set(this.adjustSuccess, key, true);
        setTimeout(() => this.$set(this.adjustSuccess, key, false), 2000);
        const newTotal = this.selectedManager.rides.reduce((s, r) => s + r.ride_amount, 0);
        this.selectedManager.total = newTotal;
        this.rows = this.rows.map(r => {
          const rKey = r.ride_id != null ? r.ride_id : (r.tx_id ? `manual_${r.tx_id}` : 'white_label');
          if (r.manager_id === this.selectedManager.manager_id && rKey === key &&
              r.year === this.selectedYear && r.month === this.selectedMonth) {
            return { ...r, ride_amount: ride.ride_amount };
          }
          return r;
        });
      } catch (e) {
        this.$set(this.adjustError, key, e.response?.data?.message || 'Save failed.');
      } finally {
        this.adjustingRideId = null;
      }
    },
    async addBillingLine() {
      if (!this.newLine.label || this.newLine.amount == null) return;
      this.addingLine = true;
      this.addLineError = null;
      try {
        const res = await billingApi.addBillingLine(this.apptoken, {
          userId: this.selectedManager.manager_id,
          year: this.selectedYear,
          month: this.selectedMonth,
          label: this.newLine.label,
          amount: this.newLine.amount,
        });
        const newRow = {
          ride_id: null,
          tx_id: res.data.tx_id,
          ride_title: this.newLine.label,
          ride_amount: this.newLine.amount,
          currency: res.data.currency,
          charged_days: null,
          participant_count: 0,
          extra_user_count: 0,
          first_registration: null,
          description: 'manual_charge',
        };
        this.selectedManager.rides.push(newRow);
        this.selectedManager.total += this.newLine.amount;
        this.newLine = { label: '', amount: null };
      } catch (e) {
        this.addLineError = e.response?.data?.message || 'Failed to add line.';
      } finally {
        this.addingLine = false;
      }
    },
    async fetchData() {
      this.loading = true;
      this.error = null;
      try {
        const response = await billingApi.getMonthlySummary(this.apptoken);
        this.rows = Array.isArray(response.data) ? response.data : [];
      } catch (err) {
        this.error = err.response?.data?.message || 'Failed to load billing summary';
      }
      this.loading = false;
      this.fetchReferrals();
    },
    async fetchReferrals() {
      // Optional section: if it fails, the billing table still works without it.
      try {
        const response = await billingApi.getReferrals(this.apptoken);
        this.referrals = Array.isArray(response.data) ? response.data : [];
      } catch (err) {
        this.referrals = [];
      }
    },
  },
};
</script>

<style scoped>
.admin-monthly-summary {
  min-height: 100vh;
  background-color: #f8f9fe;
  padding-top: 72px;
  font-size: 14px;
}

.admin-monthly-summary h3 {
  font-size: 28px;
}

.admin-monthly-summary p,
.admin-monthly-summary td,
.admin-monthly-summary th,
.admin-monthly-summary label,
.admin-monthly-summary .alert,
.admin-monthly-summary .btn {
  font-size: 14px;
}

.admin-monthly-summary .table th {
  font-size: 13px;
  padding: 10px 8px;
}

.admin-monthly-summary .table td {
  font-size: 14px;
  padding: 10px 8px;
  vertical-align: middle;
}

.admin-monthly-summary .badge {
  font-size: 12px;
  padding: 4px 8px;
}

.admin-monthly-summary small,
.admin-monthly-summary .small,
.admin-monthly-summary .text-muted {
  font-size: 12px;
}
</style>

<style>
@media (max-width: 767px) {
  .monthly-summary-modal .modal-dialog {
    margin: 0.5rem;
    max-width: calc(100% - 1rem);
  }
  .monthly-summary-modal .modal-body .table td,
  .monthly-summary-modal .modal-body .table th {
    font-size: 13px;
    padding: 8px 6px;
  }
}

.monthly-summary-modal .modal-title {
  font-size: 18px;
}
.monthly-summary-modal .modal-body p,
.monthly-summary-modal .modal-body td,
.monthly-summary-modal .modal-body th,
.monthly-summary-modal .modal-body .btn,
.monthly-summary-modal .modal-body label,
.monthly-summary-modal .modal-body span,
.monthly-summary-modal .modal-body strong,
.monthly-summary-modal .modal-body a {
  font-size: 14px;
}
.monthly-summary-modal .modal-body h6 {
  font-size: 16px;
  font-weight: 600;
}
.monthly-summary-modal .modal-body .alert {
  font-size: 14px;
  padding: 12px 16px;
}
.monthly-summary-modal .modal-body .alert strong {
  font-size: 14px;
}
.monthly-summary-modal .modal-body .table th {
  font-size: 13px;
  padding: 10px 8px;
}
.monthly-summary-modal .modal-body .table td {
  font-size: 14px;
  padding: 10px 8px;
  vertical-align: middle;
}
.monthly-summary-modal .modal-body .badge {
  font-size: 13px;
  padding: 4px 8px;
}
.monthly-summary-modal .modal-body small,
.monthly-summary-modal .modal-body .small,
.monthly-summary-modal .modal-body .text-danger.small {
  font-size: 13px;
}
</style>
